import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThumbsUp, MessageCircle, CheckSquare, Users, Star, TrendingUp } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const EngagementInteractionPage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/engagement-interaction`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Engagement & Interaction Calculators | Free Marketing Tools</title>
        <meta name="description" content="Measure customer engagement and interaction with our free calculators. Track social media engagement, form completion rates, customer satisfaction & more." />
        <meta name="keywords" content="engagement rate calculator, interaction rate, form completion rate, customer satisfaction score, NPS calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Engagement & Interaction Calculators" />
        <meta property="og:description" content="Measure and improve customer engagement with our free calculators. Track key metrics and optimize your strategy." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Engagement & Interaction Calculators" />
        <meta property="twitter:description" content="Measure and improve customer engagement with our free calculators. Track key metrics and optimize your strategy." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Engagement & Interaction</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Engagement & Interaction Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Social Media Engagement Rate" 
            description="Calculate engagement rate for social media posts" 
            link="/calculators/social-media-engagement-rate" 
            icon={ThumbsUp}
          />
          <CalculatorCard 
            title="Social Media Interaction Rate" 
            description="Measure the interaction rate on social media content" 
            link="/calculators/social-media-interaction-rate" 
            icon={MessageCircle}
          />
          <CalculatorCard 
            title="Form Completion Rate" 
            description="Calculate the rate at which users complete your forms" 
            link="/calculators/form-completion-rate" 
            icon={CheckSquare}
          />
          <CalculatorCard 
            title="Customer Survey Response Rate" 
            description="Measure the response rate for customer surveys" 
            link="/calculators/customer-survey-response-rate" 
            icon={Users}
          />
          <CalculatorCard 
            title="Customer Satisfaction Score" 
            description="Calculate customer satisfaction based on survey responses" 
            link="/calculators/customer-satisfaction-score" 
            icon={Star}
          />
          <CalculatorCard 
            title="Net Promoter Score" 
            description="Measure customer loyalty and likelihood to recommend" 
            link="/calculators/net-promoter-score" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Customer Referral Rate" 
            description="Calculate the rate at which customers refer others" 
            link="/calculators/customer-referral-rate" 
            icon={Users}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Engagement & Interaction Metrics</h2>
        <p className="text-gray-600 mb-4">
          Engagement and interaction metrics help you understand how effectively your audience connects with your brand across different touchpoints. Use these calculators to measure and improve customer engagement.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default EngagementInteractionPage;
